import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { JOURNAL_ARTICLES } from "@/data/content";
import { TESTIMONIALS } from "@/components/Testimonials";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const StorySection = ({ t, flip }) => (
  <FadeUp>
    <article
      className="grid overflow-hidden border border-jawa-stone bg-white lg:grid-cols-12"
      data-testid={`testimonial-${t.id}`}
    >
      <div className={`relative lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <img
          src={t.image}
          alt={t.imageAlt || t.name}
          loading="lazy"
          decoding="async"
          className="h-72 w-full object-cover lg:h-full lg:min-h-[420px]"
        />
      </div>
      <div className={`flex flex-col p-8 lg:col-span-7 lg:p-14 ${flip ? "lg:order-1" : ""}`}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-jawa-gold">Customer Story</span>
          <span className="border border-jawa-turquoise/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-jawa-espresso/70" data-testid={`testimonial-journey-${t.id}`}>
            {t.journey}
          </span>
        </div>
        <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-jawa-espresso lg:text-4xl">
          {t.headline}
        </h2>
        <Quote className="mt-6 h-6 w-6 shrink-0 text-jawa-coral" />
        <p className="mt-4 max-w-2xl text-base leading-loose text-jawa-espresso/80" data-testid={`testimonial-quote-${t.id}`}>
          {t.quote}
        </p>
        <p className="mt-6 max-w-2xl border-l-2 border-jawa-turquoise pl-4 text-sm leading-relaxed text-jawa-espresso/60">
          <span className="font-semibold uppercase tracking-[0.15em] text-jawa-espresso">How JÀWÀ helped: </span>
          {t.helped}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-jawa-stone pt-6">
          <p className="font-serif text-xl text-jawa-espresso" data-testid={`testimonial-name-${t.id}`}>— {t.name}</p>
          <div className="text-right">
            <Link
              to={t.journeyHref}
              data-testid={`testimonial-persona-link-${t.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-coral"
            >
              Explore the {t.journey.replace(" Journey", "")} persona <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            {t.id !== "glenn" && (
              <p className="mt-1 text-[10px] leading-snug text-jawa-espresso/45">
                A representative customer profile — not this customer's story.
              </p>
            )}
          </div>
        </div>
      </div>
    </article>
  </FadeUp>
);

export default function TestimonialsPage() {
  return (
    <div className="bg-jawa-ivory" data-testid="testimonials-page">
      <Nav />
      <Seo
        title="Testimonials & Customer Stories | JÀWÀ Africa"
        description="Real customer stories — investors, tourists, relocating families and expats who found their way back with JÀWÀ Africa."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="Customer stories & testimonials" title="People who found their way back." />
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-jawa-espresso/70" data-testid="testimonials-intro">
            These are customer stories — real journeys shared with permission, in our customers' own words. They are
            different from our <em>personas</em>, which are representative profiles of the types of people JÀWÀ
            supports (investors, tourists, returnees and expats). A story may reflect a similar journey to a persona,
            but the people featured here are not the personas themselves. Photographs are illustrative stock imagery,
            not pictures of the customers quoted.
          </p>
        </FadeUp>

        <div className="mt-16 space-y-12 lg:space-y-16" data-testid="testimonials-stories">
          {TESTIMONIALS.map((t, i) => (
            <StorySection key={t.id} t={t} flip={i % 2 === 1} />
          ))}
        </div>

        <FadeUp className="mt-16">
          <Link
            to="/case-study"
            data-testid="testimonials-case-study-link"
            className="group flex flex-wrap items-center justify-between gap-4 border border-jawa-gold/40 bg-jawa-espresso p-8 transition-colors hover:border-jawa-gold"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-jawa-gold">Full case study</p>
              <p className="mt-2 font-serif text-2xl font-medium text-jawa-ivory">A family Christmas in Lagos — the Smith family itinerary</p>
              <p className="mt-1 text-sm text-jawa-ivory/60">How a first trip home was planned day by day.</p>
            </div>
            <ArrowUpRight className="h-6 w-6 text-jawa-gold transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>

        <FadeUp className="mt-20">
          <SectionHeading overline="From the journal" title="Stories & insights." />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="testimonials-journal">
            {JOURNAL_ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to={`/journal/${a.slug}`}
                data-testid={`journal-card-${a.slug}`}
                className="group border border-jawa-espresso/10 bg-white transition-colors hover:border-jawa-turquoise/60"
              >
                {a.image && (
                  <div className="h-36 overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-serif text-lg font-medium leading-snug text-jawa-espresso group-hover:text-jawa-coral">{a.title}</h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-jawa-espresso/60">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
